import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, LogOut, Settings, Shield } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, Logo } from '@/components/ui';

interface AdminHeaderProps {
  onToggleSidebar?: () => void;
}

const AdminHeader = ({ onToggleSidebar }: AdminHeaderProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass dark:bg-gray-800/90 border-b border-gray-100 dark:border-gray-700">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Admin Badge */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={onToggleSidebar}
              className="md:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-dark-800 dark:hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            <Link to="/admin" className="flex items-center gap-2">
              <Logo size="sm" />
            </Link>

            {/* Admin Badge */}
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">
              <Shield className="w-4 h-4" />
              <span>{t('admin.adminMode')}</span>
            </div>
          </div>

          {/* User Menu */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Avatar
                src={user?.photoURL || undefined}
                name={user?.displayName || user?.email || 'Admin'}
                size="sm"
              />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-card border border-gray-100 dark:border-gray-700 py-2 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="font-medium text-dark-800 dark:text-white truncate">
                    {user?.displayName || 'Admin'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  <div className="mt-2 flex items-center gap-1.5 text-red-600 dark:text-red-400 text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    <span>{t('admin.adminRole')}</span>
                  </div>
                </div>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  {t('nav.settings')}
                </Link>
                <hr className="my-2 border-gray-100 dark:border-gray-700" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('common.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default AdminHeader;
